import { MapPin, Clock, Calendar, Euro, Star, Map } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: "job-etudiant" | "job-ete" | "stage" | "cdi" | "cdd";
  schedule: string | null;
  postedAt: string;
  description: string | null;
  hourlyRate?: number | null;
  isFeatured?: boolean;
}

interface JobCardProps {
  job: Job;
}

const typeLabels: Record<string, string> = {
  "job-etudiant": "Job étudiant",
  "job-ete": "Job d'été",
  stage: "Stage",
  cdi: "CDI",
  cdd: "CDD",
};

const typeColors: Record<string, string> = {
  "job-etudiant": "bg-primary/10 text-primary",
  "job-ete": "bg-warning/10 text-warning",
  stage: "bg-success/10 text-success",
  cdi: "bg-blue-500/10 text-blue-600",
  cdd: "bg-purple-500/10 text-purple-600",
};

// job_type may come as "job_etudiant" or "job-etudiant" depending on the record
const normalizeType = (t: string) => (t || "").replace(/_/g, "-");

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const JobCard = ({ job }: JobCardProps) => {
  return (
    <Card
      className={`group relative flex flex-col h-full bg-card border-border/70 shadow-xs transition-all duration-300 ease-soft hover:-translate-y-[3px] hover:shadow-md hover:border-border ${
        job.isFeatured ? "ring-1 ring-primary/20" : ""
      }`}
    >
      <CardContent className="flex flex-col flex-1 p-6 gap-5">
        {/* Header: avatar + company + featured */}
        <div className="flex items-start gap-3.5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted text-foreground/70 font-semibold text-sm">
            {getInitials(job.company || "JT")}
          </div>
          <div className="flex-1 min-w-0">
            <p className="type-eyebrow truncate">{job.company}</p>
            <h3 className="mt-1 font-semibold text-lg leading-snug tracking-[-0.015em] text-foreground line-clamp-2 transition-colors duration-200 group-hover:text-primary">
              {job.title}
            </h3>
          </div>
          {job.isFeatured && (
            <Badge variant="solid" className="shrink-0 gap-1 px-2.5">
              <Star className="h-3 w-3 fill-current" />
              <span className="text-[10px]">Sponsorisé</span>
            </Badge>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {job.description || "Aucune description disponible."}
        </p>


        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
              typeColors[normalizeType(job.type)] || "bg-muted text-foreground"
            }`}
          >
            {typeLabels[normalizeType(job.type)] || job.type}
          </span>
          {job.hourlyRate && (
            <span className="inline-flex items-center gap-1 rounded-full bg-success/10 text-success px-3 py-1 text-xs font-semibold">
              <Euro className="h-3 w-3" />
              {job.hourlyRate.toFixed(2)} €/h
            </span>
          )}
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground mt-auto border-t border-border/60 pt-4">
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            <span className="truncate max-w-[140px]">{job.location}</span>
          </div>
          {job.schedule && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span className="truncate max-w-[120px]">{job.schedule}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{job.postedAt}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Link to={`/offres/${job.id}`} className="flex-1">
            <Button className="w-full">Voir l'offre</Button>
          </Link>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link to={`/carte?job=${job.id}`} aria-label="Voir sur la carte">
                <Button
                  variant="outline"
                  size="icon"
                  className="min-h-[44px] min-w-[44px]"
                  aria-label="Voir sur la carte"
                >
                  <Map className="h-4 w-4" />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent>
              <p>Voir sur la carte</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobCard;
